import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Rating,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const ManagerPerformanceReview = () => {
  const { getAuthHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [ratings, setRatings] = useState({});
  const [kraComments, setKraComments] = useState({});
  const [overallComment, setOverallComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Meeting scheduling states
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [schedulingMeeting, setSchedulingMeeting] = useState(false);
  
  // Post-meeting documentation states
  const [managerCommitments, setManagerCommitments] = useState('');
  const [employeeCommitments, setEmployeeCommitments] = useState('');
  const [finalReviewDecision, setFinalReviewDecision] = useState('');
  const [overallFeedback, setOverallFeedback] = useState('');
  const [conductingMeeting, setConductingMeeting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/performance/manager/reviews`, {
        headers: getAuthHeaders()
      });
      setReviews(response.data.reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to fetch performance reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReview = async (reviewId) => {
    try {
      // Add timestamp to force fresh data and bypass cache
      const response = await axios.get(`${API_URL}/performance/reviews/${reviewId}?t=${Date.now()}`, {
        headers: {
          ...getAuthHeaders(),
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      const review = response.data.review;
      setSelectedReview(review);

      // Debug: Log the status
      console.log('Review Status:', review.status);
      console.log('Manager Submitted:', review.managerSubmitted);
      console.log('Full Review:', review);

      // Initialize ratings from existing manager ratings or create new structure
      const initialRatings = {};
      const initialKraComments = {};
      if (review.form && review.form.kras) {
        review.form.kras.forEach((kra, kraIndex) => {
          // Get KRA comment from first KPI's comment (if exists)
          if (kra.kpis.length > 0) {
            const firstKpiIndex = 0;
            const firstKpiRating = review.managerRatings?.find(
              r => r.kraIndex === kraIndex && r.kpiIndex === firstKpiIndex
            );
            if (firstKpiRating && firstKpiRating.comment) {
              initialKraComments[kraIndex] = firstKpiRating.comment;
            }
          }
          
          kra.kpis.forEach((kpi, kpiIndex) => {
            const key = `${kraIndex}-${kpiIndex}`;
            const existingRating = review.managerRatings?.find(
              r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
            );
            initialRatings[key] = existingRating ? existingRating.rating : null;
          });
        });
      }
      setRatings(initialRatings);
      setKraComments(initialKraComments);
      setOverallComment(review.managerOverallComment || '');
    } catch (error) {
      console.error('Error fetching review:', error);
      toast.error('Failed to fetch performance review');
    }
  };

  const handleRatingChange = (kraIndex, kpiIndex, value) => {
    const key = `${kraIndex}-${kpiIndex}`;
    setRatings({ ...ratings, [key]: value });
  };

  const handleSubmit = async () => {
    if (!selectedReview) return;

    // Validate all ratings are provided
    const form = selectedReview.form;
    const missingRatings = [];
    
    form.kras.forEach((kra, kraIndex) => {
      kra.kpis.forEach((kpi, kpiIndex) => {
        const key = `${kraIndex}-${kpiIndex}`;
        if (!ratings[key] || ratings[key] < 1 || ratings[key] > 5) {
          missingRatings.push(`${kra.name} - ${kpi.name}`);
        }
      });
    });

    if (missingRatings.length > 0) {
      toast.error('Please provide ratings for all KPIs');
      return;
    }

    // Convert ratings to the required format
    const ratingArray = [];
    form.kras.forEach((kra, kraIndex) => {
      const kraComment = kraComments[kraIndex] || '';
      kra.kpis.forEach((kpi, kpiIndex) => {
        const key = `${kraIndex}-${kpiIndex}`;
        // Store KRA comment in the first KPI's comment field
        ratingArray.push({
          kraIndex,
          kpiIndex,
          rating: ratings[key],
          comment: kpiIndex === 0 ? kraComment : ''
        });
      });
    });

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/manager-rating`,
        {
          ratings: ratingArray,
          overallComment
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Manager rating submitted successfully! Please schedule an appraisal meeting.');
      
      // Refresh the current review to show meeting scheduling UI
      await handleViewReview(selectedReview.reviewId);
      fetchReviews(); // Update the list in background
      
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.response?.data?.message || 'Failed to submit manager rating');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleMeeting = async () => {
    if (!meetingDate || !meetingTime) {
      toast.error('Please select both date and time for the meeting');
      return;
    }

    setSchedulingMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/schedule-meeting`,
        {
          proposedDate: meetingDate,
          proposedTime: meetingTime
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Meeting scheduled successfully. Employee will be notified.');
      fetchReviews();
      const updatedReview = await axios.get(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedReview(updatedReview.data.review);
      setMeetingDate('');
      setMeetingTime('');
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to schedule meeting');
    } finally {
      setSchedulingMeeting(false);
    }
  };

  const handleConductMeeting = async () => {
    if (!managerCommitments || !employeeCommitments || !finalReviewDecision || !overallFeedback) {
      toast.error('All post-meeting fields are required');
      return;
    }

    setConductingMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/conduct-meeting`,
        {
          managerCommitments,
          employeeCommitments,
          finalReviewDecision,
          overallFeedback
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Meeting marked as conducted. Appraisal process completed!');
      setSelectedReview(null);
      fetchReviews();
    } catch (error) {
      console.error('Error conducting meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to mark meeting as conducted');
    } finally {
      setConductingMeeting(false);
    }
  };

  const getStatusLabel = (review) => {
    switch (review.status) {
      case 'completed':
        return 'Completed';
      case 'meeting_conducted':
        return 'Meeting Conducted';
      case 'meeting_accepted':
        return 'Meeting Accepted';
      case 'meeting_scheduled':
        return 'Meeting Scheduled';
      case 'meeting_scheduling_pending':
        return 'Schedule Meeting';
      case 'reschedule_required':
        return 'Reschedule Required';
      case 'manager_submitted':
        return 'Submitted';
      case 'employee_submitted':
        return 'Pending Your Rating';
      default:
        return 'Waiting for Employee';
    }
  };

  const getStatusColor = (review) => {
    switch (review.status) {
      case 'completed':
      case 'meeting_conducted':
        return 'success';
      case 'meeting_accepted':
        return 'info';
      case 'meeting_scheduled':
      case 'meeting_scheduling_pending':
        return 'warning';
      case 'reschedule_required':
        return 'error';
      case 'manager_submitted':
        return 'info';
      case 'employee_submitted':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }

  if (selectedReview) {
    const form = selectedReview.form;
    const isSubmitted = selectedReview.managerSubmitted;
    const employeeSubmitted = selectedReview.employeeSubmitted;
    const isCompleted = selectedReview.status === 'completed';
    const needsScheduling = selectedReview.status === 'meeting_scheduling_pending';
    const meetingScheduled = selectedReview.status === 'meeting_scheduled';
    const needsReschedule = selectedReview.status === 'reschedule_required';
    const meetingAccepted = selectedReview.status === 'meeting_accepted';
    const meetingConducted = selectedReview.appraisalMeeting?.meetingConducted;

    return (
      <Box p={3}>
        <Button
          variant="outlined"
          onClick={() => setSelectedReview(null)}
          sx={{ mb: 2 }}
        >
          ← Back to Reviews
        </Button>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Review Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Employee</Typography>
                <Typography variant="body1">
                  {selectedReview.employee?.firstName} {selectedReview.employee?.lastName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedReview.employee?.email}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Review Cycle</Typography>
                <Typography variant="body1">{form.reviewCycle} {form.reviewYear}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={isCompleted ? 'Completed' : isSubmitted ? 'Submitted' : employeeSubmitted ? 'Pending Your Rating' : 'Waiting for Employee'}
                  color={isCompleted ? 'success' : isSubmitted ? 'info' : employeeSubmitted ? 'warning' : 'default'}
                />
              </Grid>
              {isCompleted && selectedReview.calculatedScores && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Final Score</Typography>
                  <Typography variant="h6" color="primary">
                    {selectedReview.calculatedScores.finalScore.toFixed(2)}/5.00
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {!employeeSubmitted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Waiting for employee to submit their self-assessment.
          </Alert>
        )}

        {isCompleted && selectedReview.calculatedScores && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Performance Score Breakdown</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>KRA</TableCell>
                      <TableCell align="right">Weightage</TableCell>
                      <TableCell align="right">Average Score</TableCell>
                      <TableCell align="right">Weighted Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedReview.calculatedScores.kraAverages.map((kraAvg) => {
                      const kra = form.kras[kraAvg.kraIndex];
                      const weightedScore = selectedReview.calculatedScores.weightedScores.find(
                        ws => ws.kraIndex === kraAvg.kraIndex
                      );
                      return (
                        <TableRow key={kraAvg.kraIndex}>
                          <TableCell>{kra.name}</TableCell>
                          <TableCell align="right">{kra.weightage}%</TableCell>
                          <TableCell align="right">{kraAvg.average.toFixed(2)}</TableCell>
                          <TableCell align="right">{weightedScore?.wts.toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell><strong>Final Performance Score</strong></TableCell>
                      <TableCell align="right"></TableCell>
                      <TableCell align="right"></TableCell>
                      <TableCell align="right">
                        <strong>{selectedReview.calculatedScores.finalScore.toFixed(2)}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Performance Review Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Performance Review Details
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 80 }}>Sr No</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 200 }}>KRA</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 300 }}>KPI / Task</TableCell>
                    {isCompleted && (
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', minWidth: 120 }}>Employee Rating</TableCell>
                    )}
                    {!isSubmitted && employeeSubmitted ? (
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', minWidth: 150 }}>Your Rating</TableCell>
                    ) : (
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', minWidth: 150 }}>Your Rating</TableCell>
                    )}
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 300 }}>KRA Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.kras.map((kra, kraIndex) => (
                    <React.Fragment key={kraIndex}>
                      {kra.kpis.map((kpi, kpiIndex) => {
                        const key = `${kraIndex}-${kpiIndex}`;
                        const currentRating = ratings[key];
                        const existingRating = selectedReview.managerRatings?.find(
                          r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
                        );
                        const employeeRating = isCompleted && selectedReview.employeeRatings?.find(
                          r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
                        );
                        const isFirstKPI = kpiIndex === 0;
                        const rowSpan = kra.kpis.length;
                        let rowNumber = 1;
                        form.kras.slice(0, kraIndex).forEach((prevKra) => {
                          rowNumber += prevKra.kpis.length;
                        });
                        rowNumber += kpiIndex;

                        return (
                          <TableRow 
                            key={kpiIndex}
                            sx={{ 
                              '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                              '&:hover': { bgcolor: 'grey.100' }
                            }}
                          >
                            <TableCell align="center" sx={{ fontWeight: 'medium' }}>
                              {rowNumber}
                            </TableCell>
                            {isFirstKPI && (
                              <TableCell 
                                rowSpan={rowSpan} 
                                sx={{ 
                                  fontWeight: 'bold',
                                  bgcolor: 'primary.50',
                                  verticalAlign: 'top',
                                  borderRight: '2px solid',
                                  borderColor: 'primary.main'
                                }}
                              >
                                {kraIndex + 1}. {kra.name}
                                {kra.description && (
                                  <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                                    {kra.description}
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {kpi.name}
                              </Typography>
                              {kpi.description && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                  {kpi.description}
                                </Typography>
                              )}
                            </TableCell>
                            {isCompleted && (
                              <TableCell align="center">
                                <Typography variant="body1" fontWeight="medium">
                                  {employeeRating?.rating || 'N/A'}/5
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell align="center">
                              {!isSubmitted && employeeSubmitted ? (
                                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                  <Rating
                                    value={currentRating || 0}
                                    onChange={(event, newValue) => {
                                      handleRatingChange(kraIndex, kpiIndex, newValue);
                                    }}
                                    max={5}
                                    size="medium"
                                  />
                                  {currentRating && (
                                    <Typography variant="body2" color="textSecondary">
                                      {currentRating}/5
                                    </Typography>
                                  )}
                                </Box>
                              ) : isSubmitted ? (
                                <Typography variant="body1" fontWeight="medium">
                                  {existingRating?.rating || 'N/A'}/5
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  Waiting for employee
                                </Typography>
                              )}
                            </TableCell>
                            {isFirstKPI && (
                              <TableCell
                                rowSpan={rowSpan}
                                sx={{
                                  verticalAlign: 'top',
                                  bgcolor: 'grey.50'
                                }}
                              >
                                {!isSubmitted && employeeSubmitted ? (
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={Math.max(3, rowSpan * 2)}
                                    value={kraComments[kraIndex] || ''}
                                    onChange={(e) => {
                                      setKraComments({
                                        ...kraComments,
                                        [kraIndex]: e.target.value
                                      });
                                    }}
                                    placeholder={`Add your comments for ${kra.name}...`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    {kraComments[kraIndex] || 'No comment provided'}
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {!isSubmitted && employeeSubmitted && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Overall Comments</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={overallComment}
                onChange={(e) => setOverallComment(e.target.value)}
                placeholder="Add your comments about the employee's performance..."
              />
            </CardContent>
          </Card>
        )}

        {isCompleted && selectedReview.employeeOverallComment && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Employee's Comments</Typography>
              <Typography variant="body1">{selectedReview.employeeOverallComment}</Typography>
            </CardContent>
          </Card>
        )}

        {isCompleted && selectedReview.managerOverallComment && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Your Comments</Typography>
              <Typography variant="body1">{selectedReview.managerOverallComment}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Meeting Scheduling Section */}
        {(needsScheduling || needsReschedule) && (
          <Card sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {needsReschedule ? '⚠️ Reschedule Meeting Required' : '📅 Schedule Appraisal Meeting'}
              </Typography>
              {needsReschedule && selectedReview.appraisalMeeting?.employeeResponse && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Employee declined the meeting:</strong>
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Reason:</strong> {selectedReview.appraisalMeeting.employeeResponse.declineReason}
                  </Typography>
                  {selectedReview.appraisalMeeting.employeeResponse.alternateDate && (
                    <Typography variant="body2">
                      <strong>Suggested Alternate:</strong>{' '}
                      {new Date(selectedReview.appraisalMeeting.employeeResponse.alternateDate).toLocaleDateString()}{' '}
                      at {selectedReview.appraisalMeeting.employeeResponse.alternateTime}
                    </Typography>
                  )}
                </Alert>
              )}
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please schedule a meeting to discuss the appraisal with the employee. This is mandatory to complete the review.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Meeting Date"
                    type="date"
                    value={meetingDate}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Meeting Time"
                    type="time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleScheduleMeeting}
                  disabled={schedulingMeeting || !meetingDate || !meetingTime}
                  color="primary"
                >
                  {schedulingMeeting ? <CircularProgress size={24} /> : 'Schedule Meeting'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Meeting Status - Waiting for Employee Response */}
        {meetingScheduled && !meetingAccepted && (
          <Card sx={{  mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'info.dark' }}>
                ⏳ Waiting for Employee Confirmation
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Scheduled Date:</strong> {new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString()}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Scheduled Time:</strong> {selectedReview.appraisalMeeting.proposedTime}
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                The employee has been notified. Waiting for their confirmation.
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Completed Appraisal - Show Documentation */}
        {isCompleted && meetingConducted && selectedReview.appraisalMeeting && (
          <Card sx={{ mb: 3,  border: '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.dark' }}>
                ✅ Appraisal Process Completed
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                This appraisal has been completed and locked. The meeting was conducted on{' '}
                {new Date(selectedReview.appraisalMeeting.conductedAt).toLocaleDateString()}.
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Meeting Date</Typography>
                  <Typography variant="body1">{new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Meeting Time</Typography>
                  <Typography variant="body1">{selectedReview.appraisalMeeting.proposedTime}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Manager Commitments</Typography>
                  <Typography variant="body1" paragraph>{selectedReview.appraisalMeeting.managerCommitments}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Employee Commitments</Typography>
                  <Typography variant="body1" paragraph>{selectedReview.appraisalMeeting.employeeCommitments}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Final Review Decision</Typography>
                  <Typography variant="body1" paragraph>{selectedReview.appraisalMeeting.finalReviewDecision}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Overall Feedback / Summary</Typography>
                  <Typography variant="body1">{selectedReview.appraisalMeeting.overallFeedback}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {!isSubmitted && employeeSubmitted && (
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setSelectedReview(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'Submit Manager Rating'}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Performance Reviews - Manager View</Typography>
      
      {reviews.length === 0 ? (
        <Alert severity="info">No performance reviews assigned to you.</Alert>
      ) : (
        <Grid container spacing={3}>
          {reviews.map((review) => (
            <Grid item xs={12} md={6} key={review._id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {review.formId?.title || 'Performance Review'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {review.formId?.reviewCycle} {review.formId?.reviewYear}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Employee: {review.employeeId?.firstName} {review.employeeId?.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {review.employeeId?.email}
                  </Typography>
                  <Box mt={2}>
                    <Chip
                      label={getStatusLabel(review)}
                      color={getStatusColor(review)}
                      sx={{ mr: 1 }}
                    />
                    {review.calculatedScores?.finalScore && (
                      <Chip
                        label={`Score: ${review.calculatedScores.finalScore.toFixed(2)}/5.00`}
                        color="primary"
                      />
                    )}
                  </Box>
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      onClick={() => handleViewReview(review._id)}
                      disabled={!review.employeeSubmitted && review.status !== 'completed'}
                      fullWidth={false}
                      sx={{ mr: 1 }}
                    >
                      View Details
                    </Button>
                    {(review.status === 'meeting_scheduling_pending' || review.status === 'reschedule_required') && (
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleViewReview(review._id)}
                        startIcon={<span>📅</span>}
                      >
                        Schedule Meeting
                      </Button>
                    )}
                    {review.status === 'meeting_scheduled' && (
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={() => handleViewReview(review._id)}
                        startIcon={<span>⏳</span>}
                      >
                        Awaiting Response
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ManagerPerformanceReview;

